import React from 'react';

const RYG_BADGE = {
  red: 'bg-danger',
  yellow: 'bg-warning text-dark',
  green: 'bg-success',
};

const RYG_OPTION_TEXT = {
  risk_awareness: {
    red: "I'm blissfully unaware",
    yellow: "I think I've got it covered, but I worry I missed something",
    green: "I'm aware of the risks that relate to my financial situation",
  },
  investment_alignment: {
    red: "I'm in an employer sponsored retirement plan",
    yellow: "I'm not sure how my investment choices support and impact my financial goals",
    green: 'I have a custom-tailored investment strategy developed with my financial plan in mind',
  },
  growth_satisfaction: {
    red: "I'm disappointed with my growth, or I don't measure my return",
    yellow: 'My assets are growing... but is it enough?',
    green: "I'm getting the growth I need to fund my dreams",
  },
  investment_care: {
    red: 'I feel my investments are being neglected by myself or others',
    yellow: "I know it's important and I should be doing more",
    green: 'I am either regularly reviewing my investments, or I have a focused, diligent team that is taking great care of my investments',
  },
};

const FREQUENCY_LABELS = {
  annually: 'Annually',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
  weekly: 'Weekly',
  daily: 'Daily',
};

const fmtText = (value) => {
  if (value == null) return '—';
  const text = String(value).trim();
  return text ? text : '—';
};

const fmtYesNo = (value) => {
  if (value === 'yes') return 'Yes';
  if (value === 'no') return 'No';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return '—';
};

const fmtFrequency = (value) => FREQUENCY_LABELS[String(value || '').toLowerCase()] || fmtText(value);

const fmtRyg = (sectionKey, value) => {
  const answer = String(value || '').toLowerCase();
  if (!answer) return <span className="text-muted">Not answered</span>;

  return (
    <span className="d-inline-flex align-items-center gap-2 flex-wrap">
      <span className={`badge ${RYG_BADGE[answer] || 'bg-secondary'}`}>{answer.toUpperCase()}</span>
      {RYG_OPTION_TEXT[sectionKey]?.[answer] && (
        <span className="text-muted">{RYG_OPTION_TEXT[sectionKey][answer]}</span>
      )}
    </span>
  );
};

const NotesRow = ({ value }) => {
  if (!value) return null;
  return (
    <div className="alert alert-warning py-2 px-3 mt-2 mb-0">
      <strong className="me-2">Notes:</strong>
      <span>{value}</span>
    </div>
  );
};

export default function CheckinFormView({ formData }) {
  if (!formData || Object.keys(formData).length === 0) {
    return <p className="text-muted fst-italic mb-0">No form data submitted yet.</p>;
  }

  const contact = formData.contact || {};
  const accounts = formData.accounts_beneficiaries || {};
  const portfolio = formData.portfolio_monitoring || {};

  return (
    <div>
      <div className="card mb-3">
        <div className="card-header py-2"><strong>1. Contact Information</strong></div>
        <div className="card-body p-2">
          <p className="mb-1"><strong>Name:</strong> {fmtText(contact.name)}</p>
          <p className="mb-1"><strong>Email:</strong> {fmtText(contact.email)}</p>
          <p className="mb-1"><strong>What is the best phone number to reach you?</strong> {fmtText(contact.phone)}</p>
          <p className="mb-1"><strong>Do you share finances with a partner or spouse?</strong> {fmtYesNo(contact.shares_finances)}</p>
          {String(contact.shares_finances || '').toLowerCase() === 'yes' && (
            <p className="mb-1"><strong>Partner / Spouse Name:</strong> {fmtText(contact.partner_name)}</p>
          )}
          <NotesRow value={contact.notes} />
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header py-2"><strong>2. Accounts & Beneficiaries</strong></div>
        <div className="card-body p-2">
          <p className="mb-1"><strong>Have you added any investment account(s)?</strong> {fmtYesNo(accounts.added_investment_accounts)}</p>
          {String(accounts.added_investment_accounts || '').toLowerCase() === 'yes' && (
            <p className="mb-1"><strong>What accounts did you add?</strong> {fmtText(accounts.new_accounts_details)}</p>
          )}
          <p className="mb-1"><strong>Have your beneficiaries changed?</strong> {fmtYesNo(accounts.beneficiaries_changed)}</p>
          {String(accounts.beneficiaries_changed || '').toLowerCase() === 'yes' && (
            <p className="mb-1"><strong>What changed?</strong> {fmtText(accounts.beneficiaries_details)}</p>
          )}
          <NotesRow value={accounts.notes} />
        </div>
      </div>

      {[
        {
          number: 3,
          key: 'risk_awareness',
          question: 'Do you know the significant risks that could derail your finances?',
        },
        {
          number: 4,
          key: 'investment_alignment',
          question: 'How tailored are your assets and investments to your financial plan?',
        },
        {
          number: 5,
          key: 'growth_satisfaction',
          question: 'How happy are you with the growth of your assets and investments?',
        },
        {
          number: 6,
          key: 'investment_care',
          question: 'Are your assets and investments getting the respect and care they deserve?',
        },
      ].map((section) => {
        const data = formData[section.key] || {};
        return (
          <div className="card mb-3" key={section.key}>
            <div className="card-header py-2"><strong>{section.number}. {section.question}</strong></div>
            <div className="card-body p-2">
              <p className="mb-1"><strong>Selected answer:</strong> {fmtRyg(section.key, data.answer)}</p>
              <NotesRow value={data.notes} />
            </div>
          </div>
        );
      })}

      <div className="card mb-0">
        <div className="card-header py-2"><strong>7. Portfolio Monitoring</strong></div>
        <div className="card-body p-2">
          <p className="mb-1"><strong>How often are you currently checking your portfolio?</strong> {fmtFrequency(portfolio.check_frequency)}</p>
          <NotesRow value={portfolio.notes} />
        </div>
      </div>

    </div>
  );
}
